// Lib
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'immutable';
import { isEmpty } from 'lodash';

import { getLabels } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import {
    selectBoardsToFetchForCurrentPath,
    selectCurrentPathLabels,
} from '../../../../../../element/labels/labelSelectors';

// Components
import LabelListElement from './LabelListElement';

// Types
import { Label } from '../../../../../../../common/labels/labelTypes';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { fetchBoards } from '../../../../../../element/board/boardService';
import { mergeCurrentLabels } from '../labelPopupUtils';

type Props = {
    handleToggleLabelSelection: (label: Label) => void;
    handleClickEdit: (label: Label) => void;
    elements: List<ImMNElement>;
    searchString: string;
    allLabels: Label[];
};

const LabelLists = (props: Props) => {
    const { elements, searchString, allLabels, handleToggleLabelSelection, handleClickEdit } = props;

    // Some elements on ancestor boards may not have been fetched yet
    const { toFetch, isBoardFetchInProgress } = useSelector(selectBoardsToFetchForCurrentPath);
    const dispatch = useDispatch();
    useEffect(() => {
        if (!toFetch.length) return;

        dispatch(fetchBoards({ boardIds: toFetch }));
    }, [toFetch]);

    // Immediately display updates like adding new labels or deleting existing ones,
    // but don't remove labels that are no longer used while the popup is open
    const latestCurrentLabels = useSelector(selectCurrentPathLabels);
    const [currentLabels, setCurrentLabels] = React.useState(latestCurrentLabels);
    useEffect(() => {
        // While fetching, always update to the latest
        if (isBoardFetchInProgress) {
            setCurrentLabels(latestCurrentLabels);
            return;
        }

        setCurrentLabels((previousCurrentLabels) =>
            mergeCurrentLabels(previousCurrentLabels, latestCurrentLabels, allLabels),
        );
    }, [allLabels, latestCurrentLabels, isBoardFetchInProgress]);

    const allLabelsFiltered = useMemo(
        () =>
            allLabels.filter(
                (label) => isEmpty(searchString) || label.name.toLowerCase().includes(searchString.toLowerCase()),
            ),
        [allLabels, searchString],
    );

    const appliedLabelIds = elements.flatMap((element) => getLabels(element) || []) as Immutable.List<string>;

    return (
        <div className="LabelLists">
            {searchString ? (
                <div className="mb-3">
                    {allLabelsFiltered.map((label) => (
                        <LabelListElement
                            key={label.id}
                            label={label}
                            isSelected={appliedLabelIds?.includes(label.id)}
                            handleClickLabel={handleToggleLabelSelection}
                            handleClickEdit={handleClickEdit}
                            size="sm"
                        />
                    ))}
                </div>
            ) : (
                <div>
                    {currentLabels.map(({ title, labels }, index) => {
                        return (
                            <div key={index} className="mb-3">
                                {title && <p className="w-full text-left mb-1">{title}</p>}
                                {labels.map((label) => {
                                    return (
                                        <LabelListElement
                                            key={label.id}
                                            label={label}
                                            isSelected={appliedLabelIds?.includes(label.id)}
                                            handleClickLabel={handleToggleLabelSelection}
                                            handleClickEdit={handleClickEdit}
                                            size="sm"
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LabelLists;
